import { createContext, useState, useEffect, useContext } from "react";
import supabase from "../lib/supabase";

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext();

const PROFILE_SELECT =
  "id, customer_type, first_name, last_name, email, phone, " +
  "address_line1, suburb, state, postcode, country, " +
  "business_name, abn, business_phone, business_email, " +
  "monthly_billing_enabled, monthly_billing_status, " +
  "monthly_billing_suspended_reason, monthly_billing_approved_at, " +
  "stripe_customer_id, default_payment_method_id, " +
  "default_payment_method_type, default_payment_method_last4, " +
  "default_payment_method_brand, " +
  "total_orders, total_spent, created_at, " +
  "points, referral_code, referred_by_code, referral_bonus_at";

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);  // Supabase auth user
  const [profile, setProfile] = useState(null);  // customer row
  const [loading, setLoading] = useState(true);
  const [authModalOpen, setAuthModalOpen]       = useState(false);
  const [accountPanelOpen, setAccountPanelOpen] = useState(false);

  // ── Bootstrap session on mount ──
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) fetchProfile(session.user);
      setLoading(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user ?? null);
        if (session?.user) fetchProfile(session.user);
        else setProfile(null);
      },
    );
    return () => listener.subscription.unsubscribe();
  }, []);

  async function fetchProfile(authUser) {
    const { data } = await supabase
      .from("customer")
      .select(PROFILE_SELECT)
      .eq("email", authUser.email)
      .maybeSingle();
    setProfile(data);
  }

  // ── Sign Up ──
  // All fields are passed as auth metadata so the DB trigger
  // (handle_new_auth_user) creates the customer row server-side
  // with SECURITY DEFINER — no client-side insert needed.
  async function signUp({
    email,
    password,
    firstName,
    lastName,
    phone,
    accountType = "individual",
    businessName,
    abn,
    businessPhone,
    businessEmail,
    referralCodeUsed,
  }) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          first_name:          firstName          || "",
          last_name:           lastName           || "",
          phone:               phone              || "",
          customer_type:       accountType === "business" ? "business" : "normal",
          business_name:       businessName       || "",
          abn:                 abn                || "",
          business_phone:      businessPhone      || "",
          business_email:      businessEmail      || "",
          referral_code_used:  referralCodeUsed   || "",
        },
      },
    });
    return { data, error };
  }

  // ── Apply referral code (post-signup, from account settings) ──
  // Calls the SECURITY DEFINER DB function which validates + records atomically.
  async function applyReferralCode(code) {
    if (!profile?.id) return { error: "not_logged_in" };
    if (profile.referred_by_code) return { error: "already_used" };

    const { data, error } = await supabase.rpc("apply_referral_code", {
      p_code:        code.trim().toUpperCase(),
      p_referred_id: profile.id,
    });

    if (error) return { error: error.message };
    if (data !== "ok") return { error: data }; // 'invalid_code' | 'own_code' | 'already_used' | 'already_completed'

    // Refresh profile to pick up referred_by_code + points update
    await fetchProfile(user);
    return { error: null };
  }

  // ── Sign In ──
  async function signIn({ email, password }) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    return { data, error };
  }

  // ── Update Profile ──
  async function updateProfile({
    firstName,
    lastName,
    phone,
    addressLine1,
    suburb,
    state,
    postcode,
    country,
    businessName,
    abn,
    businessPhone,
    businessEmail,
  }) {
    if (!profile?.id) return { error: new Error("Not logged in") };

    const updates = {
      first_name:    firstName,
      last_name:     lastName,
      phone:         phone        || null,
      address_line1: addressLine1 || null,
      suburb:        suburb       || null,
      state:         state        || null,
      postcode:      postcode     || null,
      country:       country      || null,
    };

    // Only persist business fields for business accounts
    if (profile.customer_type === "business") {
      updates.business_name  = businessName  || null;
      updates.abn            = abn           || null;
      updates.business_phone = businessPhone || null;
      updates.business_email = businessEmail || null;
    }

    const { data, error } = await supabase
      .from("customer")
      .update(updates)
      .eq("id", profile.id)
      .select(PROFILE_SELECT)
      .single();

    if (!error && data) setProfile(data);
    return { data, error };
  }

  // ── Upgrade to Business Account ──
  async function upgradeToBusinessAccount({ businessName, abn, businessPhone, businessEmail }) {
    if (!profile?.id) return { error: new Error("Not logged in") };

    const { data, error } = await supabase
      .from("customer")
      .update({
        customer_type:  "business",
        business_name:  businessName  || null,
        abn:            abn           || null,
        business_phone: businessPhone || null,
        business_email: businessEmail || null,
      })
      .eq("id", profile.id)
      .select(PROFILE_SELECT)
      .single();

    if (!error && data) setProfile(data);
    return { data, error };
  }

  // ── Refresh profile from DB ──
  async function refreshProfile() {
    if (!user) return;
    await fetchProfile(user);
  }

  // ── Sign Out ──
  async function signOut() {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
  }

  const displayName = profile
    ? `${profile.first_name} ${profile.last_name ?? ""}`.trim()
    : user?.email?.split("@")[0] ?? "";

  const isBusiness = profile?.customer_type === "business";

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        loading,
        displayName,
        isBusiness,
        authModalOpen,
        setAuthModalOpen,
        accountPanelOpen,
        setAccountPanelOpen,
        signUp,
        signIn,
        signOut,
        updateProfile,
        upgradeToBusinessAccount,
        refreshProfile,
        applyReferralCode,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => useContext(AuthContext);
